// client/src/components/ProtectedRoute.js
import React from "react";
import { Navigate, useLocation } from "react-router-dom";

/**
 * Usage:
 * <ProtectedRoute allowedRoles={["super_admin","admin"]}>
 *   <YourPage />
 * </ProtectedRoute>
 *
 * - Redirects to /login if no valid token.
 * - If `allowedRoles` is provided, the user's role must be in the list.
 * - Preserves the intended location so you can navigate back after login.
 */
const ProtectedRoute = ({ children, allowedRoles }) => {
  const location = useLocation();

  // read from localStorage (works even without an AuthContext)
  const rawToken = localStorage.getItem("token");
  const rawRole = localStorage.getItem("adminRole");

  // treat "null", "undefined", "" as not logged in
  const hasToken =
    !!rawToken && rawToken !== "null" && rawToken !== "undefined";

  // normalize role and allowedRoles to lowercase for robust checks
  const role = (rawRole || "").toLowerCase();
  const rolesRequired = Array.isArray(allowedRoles)
    ? allowedRoles.map((r) => String(r).toLowerCase())
    : null;

  // Not authenticated → send to login, keep where they came from
  if (!hasToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Role-restricted route and role not allowed → send to a safe page (dashboard)
  if (rolesRequired && rolesRequired.length && !rolesRequired.includes(role)) {
    return <Navigate to="/" replace />;
  }

  // Authorized
  return children;
};

export default ProtectedRoute;
